/**
 * refreshCharacter — Phase D glue.
 *
 * After mutations that change a character's lanterns / inventory / premium
 * state (season claim, shop purchase, payment-success, lantern-pass
 * purchase) the in-memory `Character` cached by App.tsx must be
 * re-fetched so:
 *
 *   1. The header lantern pip in Home / Room reads the new balance.
 *   2. IconPicker can equip newly-granted cosmetics (it reads from
 *      `character.icon` + the inventory it fetches via /stats — but the
 *      saved `Character` in localStorage is the source of truth across
 *      reloads, so we must refresh it).
 *   3. The Locker reflects the new inventory without a full reload.
 *
 * Two-call refresh: /api/whoami gives us the authoritative balances
 * (lanterns + embers); /api/characters/{id} gives us the latest icon +
 * `lanterns` field on the Character row. We merge both onto the existing
 * object so transient fields (hasPin, etc.) survive.
 *
 * Pure function — caller is expected to dispatch the result via
 * `onCharacterUpdated`. Failures fall back to the existing character
 * (don't crash the UI just because a balance read flaked).
 */
import { getCharacter, whoami, type Character } from './api';

export async function refreshCharacter(current: Character): Promise<Character> {
  // Run both calls in parallel; whoami is the more reliable balance source
  // (it's a single GetItem against the meta row), getCharacter brings in
  // the rest of the row's denormalized fields.
  const [meRes, charRes] = await Promise.allSettled([
    whoami(),
    getCharacter(current.charId),
  ]);

  let next: Character = current;

  if (charRes.status === 'fulfilled') {
    next = charRes.value;
  }
  if (meRes.status === 'fulfilled' && meRes.value.charId === current.charId) {
    next = {
      ...next,
      lanterns: meRes.value.lanterns,
      embers: meRes.value.embers,
    };
  }
  return next;
}
